import { flow, toPairs, groupBy, map, sortBy, filter } from 'lodash/fp';
import { createMaxDepthValue } from '../generators/utils/groupByMeasure';

import edgeImages from './images/edges';
import hasItemMeasure from './utils/hasItemMeasure';
import groupByMeasure from './utils/groupByMeasure';
import filterValidDesignText from './utils/filterValidDesignText';
import findImage, { findImagesS5 } from './utils/findImage';
import makeDisplayName from './utils/makeDisplayName';
import makeId from './utils/makeId';
import makeDisplayProductName from './utils/makeDisplayProductName';

function getEdgeImage(items, size) {
  const itemWithEdgeImage = items.find((item) => edgeImages[`${size}_${item.ItemNo}`]);
  if (itemWithEdgeImage) {
    return edgeImages[`${size}_${itemWithEdgeImage.ItemNo}`];
  } else {
    return null;
  }
}

function createExpresions(material, items, thickness2id, sinkType, measurements, country, language) {
  const productTypeName = material.productTypeName || 'custom made worktop';
  const itemsWithMaterial = items.filter((item) =>
    (material.products.includes(item.ProductNameGlobal) && (item.ProductTypeNameGlobal === productTypeName))
  );

  return flow(
    filterValidDesignText('expression', material.id),
    groupBy((item) => makeId(item, material.includeProductNameInExpression, material.id, country, language)),
    toPairs,
    map(([id, itemsWithSameDesign]) => ({
      id,
      itemNo: itemsWithSameDesign[0].ItemNo,
      displayProductName: makeDisplayProductName(itemsWithSameDesign[0], material.includeProductNameInExpression),
      displayName: makeDisplayName(itemsWithSameDesign[0]),
      displayImage: findImage(itemsWithSameDesign[0], 'S3', { ImageType: 'Quality Product Picture' }, 1),
      displayImageLarge: findImage(itemsWithSameDesign[0], 'S4', { ImageType: 'Quality Product Picture' }, 1),
      displayImagesS5: findImagesS5(itemsWithSameDesign[0], 'S5'),
      cornerImage: findImage(itemsWithSameDesign[0], 'S3', { ImageType: 'Quality Product Picture' }),
      cornerImageLarge: findImage(itemsWithSameDesign[0], 'S4', { ImageType: 'Quality Product Picture' }),
      minPrice: itemsWithSameDesign.reduce((prev, i2) => Math.min(prev, i2.price), itemsWithSameDesign[0].price),
      sinkType: sinkType(itemsWithSameDesign),
      thicknesses: groupByMeasure(itemsWithSameDesign, 'Thickness')
      .map((itemsWithSameThickness) => ({
        id: thickness2id(itemsWithSameThickness.measure),
        itemNo: itemsWithSameThickness.items[0].ItemNo,
        thickness: itemsWithSameThickness.measure,
        cornerImage: findImage(itemsWithSameThickness.items[0], 'S3', { ImageType: 'Quality Product Picture' }),
        cornerImageLarge: findImage(itemsWithSameThickness.items[0], 'S4', { ImageType: 'Quality Product Picture' }),
        functionalImage: findImage(itemsWithSameThickness.items[0], 'S4', { ImageType: 'Functional Product Picture' }),
        aluminiumEdgeImage: getEdgeImage(itemsWithSameThickness.items, 'S3'),
        aluminiumEdgeImageLarge: getEdgeImage(itemsWithSameThickness.items, 'S4'),
        priceByArea: hasItemMeasure(itemsWithSameThickness.items[0], 'Area'),
        depths: groupByMeasure(itemsWithSameThickness.items, 'Max. depth', true, measurements)
        .map((itemsWithSameDepth) =>  {
          if (itemsWithSameDepth.measure && isNaN(itemsWithSameDepth.measure)) {
            const itemMeasurements = itemsWithSameDepth.items[0].ItemMeasureList.ItemMeasure;
            const minDepthValue = itemMeasurements.find((Obj) => Obj.ItemMeasureTypeNameEn === 'Min. depth');
            if (minDepthValue) {
              const isDecentralized = itemMeasurements.find((Obj) => Obj.ItemMeasureTypeNameEn === 'Area');
              itemsWithSameDepth.measure = createMaxDepthValue(minDepthValue, isDecentralized);
            }
          }
          return {
            minLength: groupByMeasure(itemsWithSameDepth.items, 'Min. length')[0].measure,
            minDepth: groupByMeasure(itemsWithSameDepth.items, 'Min. depth')[0].measure,
            maxDepth: itemsWithSameDepth.measure,
            length: groupByMeasure(itemsWithSameDepth.items, 'Max. length')[0].measure,
            item: itemsWithSameDepth.items[0],
          }}),
      })).filter((thicknessWithId) => {
        if (!thicknessWithId.id) {
          // eslint-disable-next-line no-console
          console.error('Excluding thickness:', thicknessWithId.thickness, 'not found in business rules for items', thicknessWithId.depths.map((depth) => depth.item.ItemNo));
          return false;
        }

        return true;
      }),
    })),
    filter((expr) => {
      if (expr.thicknesses.length === 0) {
        // eslint-disable-next-line no-console
        console.error('Excluding expression:', expr.id, 'due to no available thicknesses');
        return false;
      }

      return true;
    }),
    sortBy(['minPrice', 'displayName'])
  )(itemsWithMaterial);
}


export function generateExpressions(materialId, businessRules, items, thicknesses = [], sinkType, country, language) {
  const { materials, measurements } = businessRules;
  const thickness2id = (thickness) => {
    const result = thicknesses.find((t) => thickness >= t.min && thickness <= t.max);
    return result && result.id;
  };

  const foundMaterial = materials.find((material) => material.id === materialId);

  if (foundMaterial) {
    return createExpresions(foundMaterial, items, thickness2id, sinkType, measurements, country, language);
  }

  return [];
}
